import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { MessageSquare, Plus, Search, Filter, Calendar, Tag as TagIcon, Hash, LayoutGrid, List, SlidersHorizontal, ArrowUpDown, ArrowUp, ArrowDown, MoreHorizontal, Pencil, Trash2, GripVertical, Check, X, Lock, LockOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";
import type { ChatThread, Tag } from "@shared/schema";
import { TagPicker } from "@/components/TagPicker";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { ThreadLockButton, PinVerificationDialog } from "@/components/PinVerificationDialog";
import { useToast } from "@/hooks/use-toast";

type ViewMode = "card" | "compact" | "table";
type SortField = "title" | "updatedAt" | "createdAt" | "messageCount";
type SortDirection = "asc" | "desc";

interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
}

export default function TrainsOfThought() {
  const [, setLocation] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTopic, setFilterTopic] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterLocked, setFilterLocked] = useState<string>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("card");
  const [sortField, setSortField] = useState<SortField>("updatedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedThreads, setSelectedThreads] = useState<string[]>([]);
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>("");
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pendingThreadId, setPendingThreadId] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Query for PIN status
  const { data: pinStatus } = useQuery<{ hasPinSet: boolean; isSessionValid: boolean }>({
    queryKey: ["/api/pin/status"],
  });

  const [columns, setColumns] = useState<ColumnConfig[]>([
    { id: "title", label: "Title", visible: true },
    { id: "topic", label: "Topic", visible: true },
    { id: "messages", label: "Messages", visible: true },
    { id: "tags", label: "Tags", visible: true },
    { id: "date", label: "Last Updated", visible: true },
  ]);

  const { data: threads = [], isLoading } = useQuery<ChatThread[]>({
    queryKey: ["/api/chat-threads"],
  });

  const updateThreadMutation = useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const response = await apiRequest("PATCH", `/api/chat-threads/${id}`, { title });
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
      setEditingThreadId(null);
      setEditingTitle("");
    },
  });

  const deleteThreadMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/chat-threads/${id}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
      toast({
        title: "Thread deleted",
        description: "The conversation has been permanently deleted.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete thread",
        variant: "destructive",
      });
    },
  });

  const handleDeleteThread = (threadId: string, threadTitle: string) => {
    if (window.confirm(`Are you sure you want to delete "${threadTitle || 'Untitled Conversation'}"? This action cannot be undone.`)) {
      deleteThreadMutation.mutate(threadId);
    }
  };

  // Extract unique topics from threads
  const uniqueTopics = Array.from(
    new Set(threads.map((t) => t.topic).filter(Boolean))
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleColumnToggle = (columnId: string) => {
    setColumns(columns.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  };

  const moveColumn = (fromIndex: number, toIndex: number) => {
    const newColumns = [...columns];
    const [removed] = newColumns.splice(fromIndex, 1);
    newColumns.splice(toIndex, 0, removed);
    setColumns(newColumns);
  };

  const handleSelectAll = () => {
    if (selectedThreads.length === filteredAndSortedThreads.length) {
      setSelectedThreads([]);
    } else {
      setSelectedThreads(filteredAndSortedThreads.map(t => t.id));
    }
  };

  const handleSelectThread = (threadId: string) => {
    if (selectedThreads.includes(threadId)) {
      setSelectedThreads(selectedThreads.filter(id => id !== threadId));
    } else {
      setSelectedThreads([...selectedThreads, threadId]);
    }
  };

  const handleStartEdit = (thread: ChatThread) => {
    setEditingThreadId(thread.id);
    setEditingTitle(thread.title || "");
  };

  const handleSaveEdit = () => {
    if (editingThreadId && editingTitle.trim()) {
      updateThreadMutation.mutate({ id: editingThreadId, title: editingTitle.trim() });
    }
  };

  const handleCancelEdit = () => {
    setEditingThreadId(null);
    setEditingTitle("");
  };

  // Filter threads based on search and filters
  const filteredThreads = threads.filter((thread) => {
    const threadWithTags = thread as any;
    const tagRefs = threadWithTags.tagReferences || [];
    
    const matchesSearch =
      searchQuery === "" ||
      thread.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      thread.topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tagRefs.some((tag: Tag) =>
        tag.name.toLowerCase().includes(searchQuery.toLowerCase())
      );

    const matchesTopic =
      filterTopic === "all" || thread.topic === filterTopic;

    const matchesStatus =
      filterStatus === "all" || thread.status === filterStatus;

    const matchesLocked =
      filterLocked === "all" ||
      (filterLocked === "locked" && thread.isLocked) ||
      (filterLocked === "unlocked" && !thread.isLocked);

    return matchesSearch && matchesTopic && matchesStatus && matchesLocked;
  });

  // Handle thread click with lock check
  const handleThreadClickWithLockCheck = (threadId: string, isLocked: boolean, e?: React.MouseEvent) => {
    // Don't navigate if clicking on tag picker or its children
    if (e && (e.target as HTMLElement).closest('.tag-picker-container')) {
      return;
    }
    
    // If thread is locked and PIN session is not valid, show verification dialog
    if (isLocked && !pinStatus?.isSessionValid) {
      setPendingThreadId(threadId);
      setShowPinDialog(true);
      return;
    }
    
    // Set thread ID in localStorage and navigate to chat workspace
    localStorage.setItem('currentChatThreadId', threadId);
    window.location.href = "/";
  };

  const handlePinVerifySuccess = () => {
    if (pendingThreadId) {
      localStorage.setItem('currentChatThreadId', pendingThreadId);
      window.location.href = "/";
    }
    setPendingThreadId(null);
  };

  const filteredAndSortedThreads = filteredThreads.sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case "title":
        comparison = (a.title || "").localeCompare(b.title || "");
        break;
      case "updatedAt":
        comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        break;
      case "createdAt":
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case "messageCount":
        const aCount = Array.isArray(a.messages) ? a.messages.length : 0;
        const bCount = Array.isArray(b.messages) ? b.messages.length : 0;
        comparison = aCount - bCount;
        break;
    }
    return sortDirection === "asc" ? comparison : -comparison;
  });

  const SortButton = ({ field, label }: { field: SortField; label: string }) => (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 px-2 gap-1"
      onClick={() => handleSort(field)}
      data-testid={`button-sort-${field}`}
    >
      {label}
      {sortField === field ? (
        sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
      ) : (
        <ArrowUpDown className="h-3 w-3" />
      )}
    </Button>
  );

  const handleNewChat = () => {
    setLocation("/");
  };

  const handleThreadClick = (threadId: string, e?: React.MouseEvent) => {
    // Don't navigate if clicking on tag picker or its children
    if (e && (e.target as HTMLElement).closest('.tag-picker-container')) {
      return;
    }
    // Set thread ID in localStorage and navigate to chat workspace
    localStorage.setItem('currentChatThreadId', threadId);
    window.location.href = "/"; // Force page reload to trigger hydration
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-muted-foreground">Loading chat history...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">AI Thread History</h1>
          <p className="text-muted-foreground">
            Browse and search your conversation history
          </p>
        </div>
        <Button onClick={handleNewChat} data-testid="button-new-thread">
          <Plus className="h-4 w-4 mr-2" />
          New Chat
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by title, topic, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-threads"
          />
        </div>

        <Select value={filterTopic} onValueChange={setFilterTopic}>
          <SelectTrigger className="w-[180px]" data-testid="select-filter-topic">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by topic" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Topics</SelectItem>
            {uniqueTopics.map((topic) => (
              <SelectItem key={topic} value={topic!}>
                {topic}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px]" data-testid="select-filter-status">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterLocked} onValueChange={setFilterLocked}>
          <SelectTrigger className="w-[140px]" data-testid="select-filter-locked">
            <Lock className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Lock status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Threads</SelectItem>
            <SelectItem value="locked">Locked</SelectItem>
            <SelectItem value="unlocked">Unlocked</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 border rounded-lg p-1">
          <Button
            variant={viewMode === "card" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("card")}
            data-testid="button-view-card"
            title="Expanded view"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "compact" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("compact")}
            data-testid="button-view-compact"
            title="Compact view"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "table" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("table")}
            data-testid="button-view-table"
            title="Table view"
          >
            <List className="h-4 w-4" />
          </Button>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="button-column-controls">
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[250px]">
            <DropdownMenuLabel>Manage Columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {columns.map((column, index) => (
              <div key={column.id} className="flex items-center gap-2 px-2 py-1.5 hover-elevate rounded">
                <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                <Checkbox
                  checked={column.visible}
                  onCheckedChange={() => handleColumnToggle(column.id)}
                  data-testid={`toggle-column-${column.id}`}
                />
                <span className="flex-1 text-sm">{column.label}</span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => moveColumn(index, Math.max(0, index - 1))}
                    disabled={index === 0}
                    data-testid={`move-column-up-${column.id}`}
                  >
                    <ArrowUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => moveColumn(index, Math.min(columns.length - 1, index + 1))}
                    disabled={index === columns.length - 1}
                    data-testid={`move-column-down-${column.id}`}
                  >
                    <ArrowDown className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {filteredAndSortedThreads.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <h3 className="text-lg font-semibold mb-2">No conversations found</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery || filterTopic !== "all" || filterStatus !== "all" || filterLocked !== "all"
              ? "Try adjusting your search or filters"
              : "Start a new conversation to see it here"}
          </p>
          {(searchQuery || filterTopic !== "all" || filterStatus !== "all" || filterLocked !== "all") && (
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                setFilterTopic("all");
                setFilterStatus("all");
                setFilterLocked("all");
              }}
              data-testid="button-clear-filters"
            >
              Clear Filters
            </Button>
          )}
        </Card>
      ) : viewMode === "table" ? (
        <Card>
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedThreads.length === filteredAndSortedThreads.length && filteredAndSortedThreads.length > 0}
                      onCheckedChange={handleSelectAll}
                      data-testid="checkbox-select-all"
                    />
                  </TableHead>
                  {columns.filter(c => c.visible).map(column => {
                    switch (column.id) {
                      case "title":
                        return (<TableHead key="title"><SortButton field="title" label="Title" /></TableHead>);
                      case "topic":
                        return <TableHead key="topic">Topic</TableHead>;
                      case "messages":
                        return (<TableHead key="messages"><SortButton field="messageCount" label="Messages" /></TableHead>);
                      case "tags":
                        return <TableHead key="tags">Tags</TableHead>;
                      case "date":
                        return (<TableHead key="date"><SortButton field="updatedAt" label="Last Updated" /></TableHead>);
                      default:
                        return null;
                    }
                  })}
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedThreads.map((thread) => {
                  const messageCount = Array.isArray(thread.messages) ? thread.messages.length : 0;
                  const threadWithTags = thread as any;
                  const tagRefs = threadWithTags.tagReferences || [];
                  
                  return (
                    <TableRow 
                      key={thread.id} 
                      className="hover-elevate cursor-pointer"
                      onClick={(e) => {
                        if (!(e.target as HTMLElement).closest('button, input, .tag-picker-container')) {
                          handleThreadClickWithLockCheck(thread.id, thread.isLocked || false, e);
                        }
                      }}
                      data-testid={`thread-row-${thread.id}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedThreads.includes(thread.id)}
                          onCheckedChange={() => handleSelectThread(thread.id)}
                          data-testid={`checkbox-thread-${thread.id}`}
                        />
                      </TableCell>
                      {columns.filter(c => c.visible).map(column => {
                        switch (column.id) {
                          case "title":
                            return (
                              <TableCell key="title" className="font-medium">
                                {editingThreadId === thread.id ? (
                                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                    <Input
                                      value={editingTitle}
                                      onChange={(e) => setEditingTitle(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") handleSaveEdit();
                                        if (e.key === "Escape") handleCancelEdit();
                                      }}
                                      className="h-8"
                                      autoFocus
                                      data-testid="input-edit-title"
                                    />
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleSaveEdit}>
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleCancelEdit}>
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 max-w-md">
                                    {thread.isLocked && (
                                      <Lock className="h-4 w-4 text-amber-500 shrink-0" />
                                    )}
                                    <span className="truncate">{thread.title || "Untitled Conversation"}</span>
                                  </div>
                                )}
                              </TableCell>
                            );
                          case "topic":
                            return (
                              <TableCell key="topic">
                                {thread.topic && (
                                  <Badge variant="secondary" className="text-xs">
                                    <Hash className="h-3 w-3 mr-1" />
                                    {thread.topic}
                                  </Badge>
                                )}
                              </TableCell>
                            );
                          case "messages":
                            return (
                              <TableCell key="messages">
                                <span className="text-sm">{messageCount}</span>
                              </TableCell>
                            );
                          case "tags":
                            return (
                              <TableCell key="tags" onClick={(e) => e.stopPropagation()}>
                                <div className="tag-picker-container">
                                  <TagPicker
                                    entityType="chat_thread"
                                    entityId={thread.id}
                                    onTagsChange={() => {}}
                                  />
                                </div>
                              </TableCell>
                            );
                          case "date":
                            return (
                              <TableCell key="date">
                                <span className="text-sm text-muted-foreground">
                                  {formatDistanceToNow(new Date(thread.updatedAt), { addSuffix: true })}
                                </span>
                              </TableCell>
                            );
                          default:
                            return null;
                        }
                      })}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <ThreadLockButton
                            threadId={thread.id}
                            isLocked={thread.isLocked || false}
                          />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`button-actions-${thread.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleStartEdit(thread)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit Title
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => handleDeleteThread(thread.id, thread.title || "")}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollArea>
        </Card>
      ) : (
        <div className={viewMode === "compact" ? "space-y-2" : "space-y-4"}>
          {filteredAndSortedThreads.map((thread) => {
            const messageCount = Array.isArray(thread.messages) ? thread.messages.length : 0;
            const lastMessage = Array.isArray(thread.messages) && thread.messages.length > 0
              ? thread.messages[thread.messages.length - 1]
              : null;
            const threadWithTags = thread as any;
            const tagRefs = threadWithTags.tagReferences || [];

            return (
              <Card
                key={thread.id}
                className={`hover-elevate transition-all ${viewMode === "compact" ? "p-4" : "p-6"} ${thread.isLocked ? "border-amber-500/30" : ""}`}
                data-testid={`thread-${thread.id}`}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className={`${thread.isLocked ? "bg-amber-500/10" : "bg-primary/10"} rounded-lg shrink-0 cursor-pointer ${viewMode === "compact" ? "p-2" : "p-3"}`}
                    onClick={(e) => handleThreadClickWithLockCheck(thread.id, thread.isLocked || false, e)}
                  >
                    {thread.isLocked ? (
                      <Lock className={viewMode === "compact" ? "h-4 w-4 text-amber-500" : "h-5 w-5 text-amber-500"} />
                    ) : (
                      <MessageSquare className={viewMode === "compact" ? "h-4 w-4 text-primary" : "h-5 w-5 text-primary"} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      {editingThreadId === thread.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit();
                              if (e.key === "Escape") handleCancelEdit();
                            }}
                            className="h-8"
                            autoFocus
                            data-testid="input-edit-title"
                          />
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleSaveEdit}>
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleCancelEdit}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <h3 
                            className={`font-semibold cursor-pointer hover:text-primary ${viewMode === "compact" ? "text-base" : "text-lg"}`}
                            onClick={(e) => handleThreadClickWithLockCheck(thread.id, thread.isLocked || false, e)}
                          >
                            {thread.title || "Untitled Conversation"}
                          </h3>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDistanceToNow(new Date(thread.updatedAt), { addSuffix: true })}
                            </span>
                            <ThreadLockButton
                              threadId={thread.id}
                              isLocked={thread.isLocked || false}
                            />
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`button-actions-${thread.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleStartEdit(thread)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit Title
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="text-destructive"
                                  onClick={() => handleDeleteThread(thread.id, thread.title || "")}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </>
                      )}
                    </div>

                    {viewMode === "card" && lastMessage && typeof lastMessage === 'object' && 'content' in lastMessage && (
                      <p 
                        className="text-sm text-muted-foreground mb-3 line-clamp-2 cursor-pointer"
                        onClick={(e) => handleThreadClickWithLockCheck(thread.id, thread.isLocked || false, e)}
                      >
                        {thread.isLocked && !pinStatus?.isSessionValid ? (
                          <span className="italic text-amber-500">Content is locked</span>
                        ) : (
                          <>{String(lastMessage.content).substring(0, 150)}...</>
                        )}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      {thread.topic && (
                        <Badge variant="secondary" className="text-xs">
                          <Hash className="h-3 w-3 mr-1" />
                          {thread.topic}
                        </Badge>
                      )}
                      <div className="tag-picker-container" onClick={(e) => e.stopPropagation()}>
                        <TagPicker
                          entityType="chat_thread"
                          entityId={thread.id}
                          onTagsChange={() => {}}
                        />
                      </div>
                      <Badge variant="secondary" className="text-xs ml-auto">
                        <Calendar className="h-3 w-3 mr-1" />
                        {messageCount} messages
                      </Badge>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* PIN Verification Dialog for accessing locked threads */}
      <PinVerificationDialog
        open={showPinDialog}
        onOpenChange={setShowPinDialog}
        onSuccess={handlePinVerifySuccess}
        title="Unlock Thread"
        description="Enter your PIN to access this locked thread"
      />
    </div>
  );
}
